import { Stack, TextField, type TextFieldProps } from '@mui/material';
import { _ } from '/@/locale';

const CUIT_LENGTH = 11;
const CUIT_FORMAT = [2, 8, 1] as const; // ##-########-#

const CUIT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2] as const;
const VALID_CUIT_PREFIXES = ['20', '23', '24', '27', '30', '33', '34'] as const;

export type CuitFormatError = 'empty' | 'length' | 'non_numeric' | 'prefix' | 'check_digit';

/** Checks CUIT format (11 digits) and check digit (Argentine AFIP algorithm). Returns why it fails, or null. */
export function cuitFormatError(digits: string): CuitFormatError | null {
    if (!digits || digits.length === 0) return 'empty';
    if (digits.length !== CUIT_LENGTH) return 'length';
    if (!/^\d+$/.test(digits)) return 'non_numeric';
    if (!VALID_CUIT_PREFIXES.includes(digits.slice(0, 2) as (typeof VALID_CUIT_PREFIXES)[number])) return 'prefix';
    let total = 0;
    for (let i = 0; i < 10; i++) {
        const w = CUIT_WEIGHTS[i];
        total += Number(digits.charAt(i)) * (w ?? 0);
    }
    let check = 11 - (total % 11);
    if (check === 11) check = 0;
    else if (check === 10) check = 9;
    if (Number(digits.charAt(10)) !== check) return 'check_digit';
    return null;
}

const cuitFormatErrorMessage = (error: CuitFormatError): string => {
    switch (error) {
        case 'empty':
            return _('Ingresá el número de CUIT');
        case 'length':
            return _('El CUIT tiene que tener 11 dígitos.');
        case 'non_numeric':
            return _('El CUIT debe contener solo números');
        case 'prefix':
            return _('El prefijo del CUIT debe ser 20, 23, 24, 27 (personas) o 30, 33, 34 (empresas)');
        case 'check_digit':
            return _('El dígito verificador del CUIT es incorrecto');
    }
};

/**
 * Validates CUIT format (11 digits) and check digit (Argentine AFIP algorithm).
 * Returns an error message key or null if valid.
 */
export function validateCuit(digits: string): string | null {
    const error = cuitFormatError(digits);
    return error ? cuitFormatErrorMessage(error) : null;
}

function formatCuitDisplay(digits: string): string {
    if (digits.length === 0) return '';
    let i = 0;
    const parts: string[] = [];
    for (const n of CUIT_FORMAT) {
        const part = digits.slice(i, i + n);
        i += n;
        if (part.length === 0) break;
        parts.push(part);
    }
    return parts.join('-');
}

function parseCuitDigits(value: string): string {
    return value.replace(/\D/g, '').slice(0, CUIT_LENGTH);
}

export type CuitTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
    value: string;
    onChange: (digits: string) => void;
};

const CuitTextField = ({ value, onChange, error, helperText, InputProps, ...textFieldProps }: CuitTextFieldProps) => {
    const displayValue = formatCuitDisplay(value);
    const digitCount = value.length;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(parseCuitDigits(e.target.value));
    };

    return (
        <TextField
            label={_('CUIT')}
            fullWidth
            size='small'
            placeholder='00-00000000-0'
            value={displayValue}
            onChange={handleChange}
            inputProps={{ inputMode: 'numeric' }}
            error={error}
            helperText={
                helperText != null ? (
                    helperText
                ) : (
                    <Stack direction='row' justifyContent='space-between' component='span'>
                        <span>{`${_('Ingresá el número de CUIT sin guiones')} (${digitCount} / ${CUIT_LENGTH})`}</span>
                    </Stack>
                )
            }
            InputProps={InputProps}
            {...textFieldProps}
        />
    );
};

export default CuitTextField;

export { CUIT_LENGTH, formatCuitDisplay, parseCuitDigits };
